import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { auth, GroupRule, groupRules, setTokens } from "../api/client";
import { useTour } from "../tutorial/Tour";

function ProfileCard() {
  const { user, refreshUser } = useAuth();
  const [fullName, setFullName] = useState(user?.full_name ?? "");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  async function save(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setNote(null);
    try {
      const res = await auth.updateMe({
        full_name: fullName,
        ...(newPassword ? { current_password: currentPassword, new_password: newPassword } : {}),
      });
      // A new password ends every other session; keep this one with fresh tokens.
      if (res.access_token && res.refresh_token) setTokens(res.access_token, res.refresh_token);
      await refreshUser();
      setCurrentPassword("");
      setNewPassword("");
      setNote("Saved.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="detail-card">
      <h3>Account</h3>
      {error && <div className="page-error">{error}</div>}
      <form onSubmit={save} className="auth-form" style={{ maxWidth: 420 }}>
        <label>
          Email
          <input type="email" value={user?.email ?? ""} disabled />
        </label>
        <label>
          Full name
          <input type="text" value={fullName} onChange={(e) => setFullName(e.target.value)} />
        </label>
        <label>
          Current password
          <input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)}
            placeholder="Only needed to change your password" />
        </label>
        <label>
          New password
          <input type="password" minLength={8} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
        </label>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </button>
          {note && <span className="muted">{note}</span>}
        </div>
      </form>
    </div>
  );
}

const FIELD_LABEL: Record<string, string> = {
  receipt_type: "type",
  currency: "currency",
  card_brand: "card brand",
  card_last4: "card",
};

function describeRule(r: GroupRule): string {
  const vendorSource = r.source_vendor_name || "a vendor";
  switch (r.rule_type) {
    case "vendor_rename":
      return `Rename “${vendorSource}” → “${r.target_vendor_name}”`;
    case "vendor_combine":
      return `Combine “${vendorSource}” into “${r.target_vendor_name}”`;
    case "vendor_match":
      return `“${vendorSource}” is “${r.target_vendor_name}” (matched by AI)`;
    case "vendor_field":
      return `Always set ${FIELD_LABEL[r.field_name ?? ""] ?? r.field_name} to “${r.field_value}” for “${vendorSource}”`;
    case "card_combine": {
      const src = `${r.source_card_brand ?? "Card"} ····${r.source_card_last4}`;
      const dst = `${r.target_card_brand ?? "Card"} ····${r.target_card_last4}`;
      return `Combine card ${src} into ${dst}`;
    }
    default:
      return r.rule_type;
  }
}

function GroupingRulesCard() {
  const [rules, setRules] = useState<GroupRule[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  function load() {
    groupRules
      .list()
      .then(setRules)
      .catch((e) => setError(e instanceof Error ? e.message : "Could not load rules"));
  }

  useEffect(load, []);

  async function remove(id: string) {
    if (!confirm("Delete this grouping rule? Future receipts will no longer be grouped by it. Receipts already grouped stay as they are.")) {
      return;
    }
    setBusyId(id);
    setError(null);
    try {
      await groupRules.remove(id);
      setRules((prev) => (prev ? prev.filter((r) => r.id !== id) : prev));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not delete rule");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="detail-card">
      <h3>Vendor &amp; card grouping rules</h3>
      <p style={{ color: "var(--text-soft)", marginTop: 0 }}>
        When you rename a vendor or combine vendors/cards, or the AI recognises a
        new spelling of a vendor or client you already have, we save a rule that
        re-applies the same grouping to future receipts after parsing. Delete a
        rule here to stop it applying.
      </p>
      {error && <div className="page-error">{error}</div>}
      {rules === null ? (
        <div className="page-empty">Loading…</div>
      ) : rules.length === 0 ? (
        <div className="page-empty">
          No grouping rules yet. Rename or combine a vendor/card to create one.
        </div>
      ) : (
        <table className="data-table">
          <tbody>
            {rules.map((r) => (
              <tr key={r.id}>
                <td>{describeRule(r)}</td>
                <td style={{ textAlign: "right", width: 90 }}>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    disabled={busyId === r.id}
                    onClick={() => remove(r.id)}
                  >
                    {busyId === r.id ? "Deleting…" : "Delete"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default function Settings() {
  const tour = useTour();
  const [tourError, setTourError] = useState<string | null>(null);

  const [starting, setStarting] = useState(false);

  async function takeTour() {
    if (starting) return;
    setStarting(true);
    setTourError(null);
    try {
      await tour.start();
    } catch (e) {
      setTourError(e instanceof Error ? e.message : "Could not start the tour");
    } finally {
      setStarting(false);
    }
  }


  return (
    <div className="page">
      <div className="page-header">
        <h1>Settings</h1>
        <button className="btn btn-ghost" onClick={takeTour} disabled={tour.active || starting}>
          Take the tour
        </button>
      </div>
      {tourError && <div className="page-error">{tourError}</div>}

      <ProfileCard />

      <GroupingRulesCard />

      <div className="detail-card">
        <h3>About Set &amp; Ledger</h3>
        <p style={{ color: "var(--text-soft)", margin: 0 }}>
          Set &amp; Ledger collects receipts, bills and income from your connected mailboxes, reads them with AI,
          and sends them to QuickBooks. Manage mailboxes on the <Link to="/app/connections">Connections</Link> page
          and the QuickBooks link on the <Link to="/app/quickbooks">QuickBooks</Link> page. See the{" "}
          <Link to="/privacy">Privacy Policy</Link> and <Link to="/terms">Terms of Service</Link>.
        </p>
      </div>
    </div>
  );
}
